import { create } from 'zustand'
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore'
import { db } from '../config/firebase'

export interface PageUserRecord {
  userId: string
  visits: number
  totalSeconds: number
  lastVisitAt: Date | null
}

interface ScreenStats {
  totalViews: number
  totalDurationSeconds: number
}

interface SessionRecord {
  startedAt: Date
  endedAt: Date | null
  durationSeconds: number
  isActive: boolean
  userId: string
  userName?: string
  /** 'idle_timeout' | 'logout' | 'app_terminate' | 'server_cleanup' | 'client_restart' | undefined */
  closedBy?: string
}

interface AnalyticsState {
  pageViewStats: Record<string, ScreenStats> | null
  sessionRecords: SessionRecord[]
  idleTimeoutMinutes: number
  pageUserBreakdown: Record<string, PageUserRecord[]>
  loading: boolean
  error: string | null
  fetchPageViewStats: () => Promise<void>
  fetchIdleTimeout: () => Promise<void>
  updateIdleTimeout: (minutes: number) => Promise<void>
  fetchAllSessions: () => Promise<void>
  fetchPageUserBreakdown: (screenName: string) => Promise<void>
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  pageViewStats: null,
  sessionRecords: [],
  idleTimeoutMinutes: 10,
  pageUserBreakdown: {},
  loading: false,
  error: null,

  fetchPageViewStats: async () => {
    set({ loading: true, error: null })
    try {
      const snap = await getDoc(doc(db, 'analytics', 'pageViewStats'))
      if (snap.exists()) {
        const data = snap.data()
        // Cloud Function 會寫入 { screens: { screenName: { totalViews, totalDurationSeconds } } }
        const screensData = data.screens as Record<string, Record<string, number>> | undefined
        const IGNORED_SCREENS = new Set(['main', 'splash'])
        const screens: Record<string, ScreenStats> = {}
        if (screensData) {
          for (const [screenName, stats] of Object.entries(screensData)) {
            if (IGNORED_SCREENS.has(screenName)) continue
            if (stats && typeof stats === 'object') {
              screens[screenName] = {
                totalViews: (stats.totalViews as number) || 0,
                totalDurationSeconds: (stats.totalDurationSeconds as number) || 0,
              }
            }
          }
        }
        set({ pageViewStats: screens, loading: false })
      } else {
        set({ pageViewStats: {}, loading: false })
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },

  fetchIdleTimeout: async () => {
    try {
      const snap = await getDoc(doc(db, 'settings', 'analytics'))
      if (snap.exists()) {
        const data = snap.data()
        if (typeof data.idleTimeoutMinutes === 'number') {
          set({ idleTimeoutMinutes: data.idleTimeoutMinutes })
        }
      }
    } catch (err) {
      console.error('Failed to fetch idle timeout:', err)
    }
  },

  updateIdleTimeout: async (minutes: number) => {
    try {
      await setDoc(
        doc(db, 'settings', 'analytics'),
        { idleTimeoutMinutes: minutes, updatedAt: new Date() },
        { merge: true }
      )
      set({ idleTimeoutMinutes: minutes })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  fetchAllSessions: async () => {
    set({ loading: true, error: null })
    try {
      // 取得所有使用者
      const usersSnap = await getDocs(collection(db, 'users'))
      const allSessions: SessionRecord[] = []

      for (const userDoc of usersSnap.docs) {
        const userData = userDoc.data()
        const userName = userData.displayName || userDoc.id
        const sessionsSnap = await getDocs(
          collection(db, 'users', userDoc.id, 'sessions')
        )
        for (const sessionDoc of sessionsSnap.docs) {
          const data = sessionDoc.data()
          allSessions.push({
            userId: userDoc.id,
            userName,
            startedAt: data.startedAt?.toDate() || new Date(),
            endedAt: data.endedAt?.toDate() || null,
            durationSeconds: data.durationSeconds || 0,
            isActive: data.isActive || false,
            closedBy: data.closedBy,
          })
        }
      }

      // 依開始時間降序排列
      allSessions.sort(
        (a, b) => b.startedAt.getTime() - a.startedAt.getTime()
      )

      set({ sessionRecords: allSessions, loading: false })
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },

  fetchPageUserBreakdown: async (screenName: string) => {
    const existing = get().pageUserBreakdown[screenName]
    if (existing) return
    try {
      const snap = await getDocs(
        collection(db, 'analytics', 'pageUserBreakdown', screenName)
      )
      const records: PageUserRecord[] = snap.docs.map((d) => ({
        userId: d.id,
        visits: d.data().visits ?? 0,
        totalSeconds: d.data().totalSeconds ?? 0,
        lastVisitAt: d.data().lastVisitAt?.toDate() ?? null,
      }))
      records.sort((a, b) => b.totalSeconds - a.totalSeconds)
      set((s) => ({
        pageUserBreakdown: { ...s.pageUserBreakdown, [screenName]: records },
      }))
    } catch (err) {
      console.error('fetchPageUserBreakdown failed:', err)
    }
  },
}))
